import React, { useState } from 'react';
import {
    LucideMail,
    LucideUser,
    LucidePhone,
    LucideUpload,
    LucideSend,
    LucideCheckCircle,
    LucideXCircle,
    LucideRefreshCcw,
} from 'lucide-react';
const URL = process.env.REACT_APP_API_URL;

const App = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        department: '',
        coverLetter: '',
        cvFile: null,
        consent: false,
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setError('');

        if (!formData.name || !formData.email || !formData.department || !formData.cvFile || !formData.consent) {
            setError('Lütfen tüm zorunlu alanları doldurun ve CV yükleyin.');
            setLoading(false);
            return;
        }

        const data = new FormData();
        data.append('name', formData.name);
        data.append('email', formData.email);
        data.append('phone', formData.phone);
        data.append('department', formData.department);
        data.append('coverLetter', formData.coverLetter);
        data.append('cv_file_path', formData.cvFile);
        data.append('consent', formData.consent ? 'true' : 'false');
        
        try {
            const response = await fetch(`${URL}/api/applications`, {
                method: 'POST',
                body: data,
            });

            if (response.ok) {
                const result = await response.json();
                setMessage(result.message || 'Başvurunuz başarıyla gönderildi! Teşekkür ederiz.');
                
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    department: '',
                    coverLetter: '',
                    cvFile: null,
                    consent: false,
                });
            } else {
                const errorData = await response.json();
                console.error('Backend Hata Detayı:', errorData);

                let errorMessage = 'Başvuru gönderilirken bir hata oluştu.';

                if (errorData.detail) {
                    if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
                        const firstError = errorData.detail[0];
                        const fieldName = firstError.loc[firstError.loc.length - 1];
                        errorMessage = `Hata: ${fieldName} alanında bir sorun var. Mesaj: ${firstError.msg}`;
                    } else if (typeof errorData.detail === 'string') {
                        errorMessage = errorData.detail;
                    }
                }
                
                setError(errorMessage);
            }
        } catch (err) {
            console.error('Ağ hatası:', err);
            setError('Ağa bağlanırken bir sorun oluştu. Lütfen sunucunun çalıştığından emin olun.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 text-slate-200 font-sans p-4 sm:p-8 flex items-center justify-center">
            <div className="w-full max-w-2xl mx-auto">
                {/* Sayfa başlığı ve alt metni */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500">
                        Ekibimize Katılın
                    </h1>
                    <p className="text-lg text-slate-400">
                        Mekânname'nin heyecan verici yolculuğunda bize eşlik etmek ister misiniz? Başvurunuzu aşağıdan yapabilirsiniz.
                    </p>
                </div>

                {/* Başvuru formu */}
                <form
                    onSubmit={handleSubmit}
                    className="bg-slate-800 p-6 sm:p-10 rounded-2xl shadow-xl space-y-6"
                >
                    {/* Form durumu mesajları */}
                    {message && (
                        <div className="flex items-center justify-center bg-teal-800 text-teal-200 p-4 rounded-xl mb-6 shadow-md">
                            <LucideCheckCircle className="w-6 h-6 mr-3" />
                            <p className="font-medium">{message}</p>
                        </div>
                    )}
                    {error && (
                        <div className="flex items-center justify-center bg-red-800 text-red-200 p-4 rounded-xl mb-6 shadow-md">
                            <LucideXCircle className="w-6 h-6 mr-3" />
                            <p className="font-medium">{error}</p>
                        </div>
                    )}

                    {/* Ad Soyad alanı */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                            Ad Soyad <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                                <LucideUser size={20} />
                            </span>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full pl-10 pr-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-colors duration-200"
                                placeholder="Adınız Soyadınız"
                            />
                        </div>
                    </div>

                    {/* E-posta alanı */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                            E-posta <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                                <LucideMail size={20} />
                            </span>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full pl-10 pr-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-colors duration-200"
                                placeholder="ornek@mail.com"
                            />
                        </div>
                    </div>

                    {/* Telefon alanı */}
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
                            Telefon
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                                <LucidePhone size={20} />
                            </span>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full pl-10 pr-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-colors duration-200"
                                placeholder="(5xx) xxx xx xx"
                            />
                        </div>
                    </div>

                    {/* İlgilenilen Pozisyon alanı */}
                    <div>
                        <label htmlFor="department" className="block text-sm font-medium text-slate-300 mb-2">
                            İlgilendiğiniz Pozisyon <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="department"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-colors duration-200"
                        >
                            <option value="" disabled>Lütfen bir pozisyon seçin</option>
                            <option value="software_engineer">Yazılım Mühendisi</option>
                            <option value="marketing_specialist">Pazarlama Uzmanı</option>
                            <option value="content_editor">İçerik Editörü</option>
                            <option value="other">Diğer</option>
                        </select>
                    </div>

                    {/* CV Yükleme alanı */}
                    <div>
                        <label htmlFor="cvFile" className="block text-sm font-medium text-slate-300 mb-2">
                            CV Yükle <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <label
                                htmlFor="cvFile"
                                className="w-full flex items-center justify-between px-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-400 cursor-pointer hover:bg-slate-600 transition-colors duration-200"
                            >
                                <span>
                                    {formData.cvFile ? formData.cvFile.name : 'Dosya seçin (PDF, DOCX)'}
                                </span>
                                <LucideUpload size={20} className="text-teal-400" />
                            </label>
                            <input
                                type="file"
                                id="cvFile"
                                name="cvFile"
                                onChange={handleChange}
                                accept=".pdf,.doc,.docx"
                                className="hidden"
                                required
                            />
                        </div>
                    </div>

                    {/* Ön Yazı alanı */}
                    <div>
                        <label htmlFor="coverLetter" className="block text-sm font-medium text-slate-300 mb-2">
                            Ön Yazı / Mesaj
                        </label>
                        <textarea
                            id="coverLetter"
                            name="coverLetter"
                            value={formData.coverLetter}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-colors duration-200"
                            placeholder="Neden Mekanname.com'da çalışmak istediğinizi kısaca anlatın..."
                        ></textarea>
                    </div>

                    {/* KVKK Onay kutusu */}
                    <div className="flex items-start">
                        <input
                            type="checkbox"
                            id="consent"
                            name="consent"
                            checked={formData.consent}
                            onChange={handleChange}
                            required
                            className="h-4 w-4 mt-1 rounded text-teal-500 bg-slate-700 border-slate-600 focus:ring-teal-400"
                        />
                        <label htmlFor="consent" className="ml-3 text-sm text-slate-400">
                            <a href="#" className="underline hover:text-white">KVKK Aydınlatma Metni'ni</a> okudum ve kişisel verilerimin işlenmesine onay veriyorum.
                        </label>
                    </div>

                    {/* Gönder butonu */}
                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 px-6 bg-teal-500 text-white font-bold rounded-lg shadow-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 focus:ring-offset-slate-900 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <LucideRefreshCcw className="animate-spin mr-2 h-5 w-5" />
                                    <span>Gönderiliyor...</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center">
                                    <LucideSend className="mr-2 h-5 w-5" />
                                    <span>Başvuruyu Gönder</span>
                                </div>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default App;
